import { Location } from "@angular/common"
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    HostBinding,
    OnInit,
    ViewEncapsulation,
} from "@angular/core"
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from "@angular/router"
import { NgbTooltipConfig } from "@ng-bootstrap/ng-bootstrap"
import { NotificationsService } from "angular2-notifications"

import { MessageService } from "./services/message.service"
import { PendoService } from "./services/pendo.service"
import { UserService } from "./services/user.service"
import { ModalService } from "./shared-components/modal/modal.service"
import { AuthModalComponent } from "./shared-components/modals/auth-modal/auth-modal.component"
import { NotificationModalComponent } from "./shared-components/modals/notification-modal/notification-modal.component"
import { StudentLoginModalComponent } from "./shared-components/modals/student-login-modal/student-login-modal.component"
import { redirectToError } from "./utils/error-handling"
import Utils from "./utils/utils"
import { AddOnViewService } from "./services/add-on-view.service"
import { StudentGateService } from "./services/student-gate.service"

declare var window: any

const addOnLoginRoute = "/add-on/log-in"

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, AfterViewInit {
    @HostBinding("class") hostBinding = "app-wrapper"

    isDisplayed = true
    isNavigationStopped: boolean = false
    constructor(
        private router: Router,
        private activeRoute: ActivatedRoute,
        private modalService: ModalService,
        private userService: UserService,
        private notificationService: NotificationsService,
        private pendoService: PendoService,
        private tooltipConfig: NgbTooltipConfig,
        private location: Location,
        private addOnService: AddOnViewService,
        private messageService: MessageService,
        private studentGateService: StudentGateService,
        private cdr: ChangeDetectorRef
    ) {
        window.PBSLM.browser = {
            ie: Utils.isIEBrowser(),
            safari: Utils.isSafari(),
            edge: Utils.isEdgeBrowser(),
        }
        if (window.PBSLM.ERROR_CODE) {
            redirectToError(this.router, this.location, window.PBSLM.ERROR_CODE)
            window.PBSLM.ERROR_CODE = null
            return
        }

        this.configureTooltip()
    }

    public ngOnInit() {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                // for add-on login route we don't display header and footer
                if (this.addOnService.isAddonView && this.router.url.startsWith(addOnLoginRoute)) {
                    this.isDisplayed = false
                }

                const url = new URL(this.router.url, window.location.origin)
                const urlFragments = url.pathname.split("/")
                const studentQueryParam = !!url.searchParams.get("student")
                if (urlFragments[1] && urlFragments[1] === "") {
                    // Homepage case
                    this.userService.setStudentExperience(false)
                } else if (urlFragments[1] && urlFragments[1] === "student") {
                    // Student urls
                    this.userService.setStudentExperience(true)
                    //case for assignments and classes when paste the url without query param
                    if (urlFragments[2] && !studentQueryParam) {
                        this.router.navigate([], {
                            queryParams: { student: true },
                        })
                    }
                } else if (this.userService.isStudent() && !studentQueryParam) {
                    // student is logged in, should always have student experience and student=true
                    // but we need to keep existing params for error navigation for example
                    this.router.navigate([], {
                        queryParams: { student: true },
                        queryParamsHandling: "merge",
                        skipLocationChange: true,
                    })
                } else {
                    // student=true url case
                    this.userService.setStudentExperience(studentQueryParam)
                }
            }
        })

        this.userService.runOnceForQueryParam(this.activeRoute, "login", () => {
            if (!this.userService.isLoggedIn()) {
                if (this.userService.hasStudentExperience()) {
                    this.modalService.open(StudentLoginModalComponent)
                } else {
                    this.modalService.open(AuthModalComponent)
                }
            }
        })

        this.pendoService.initialize()
        this.scrollToTopBeforeUnload()
    }

    private scrollToTopBeforeUnload() {
        window.onbeforeunload = () => {
            document.querySelector("html").style.scrollBehavior = "auto"
            document.documentElement.scrollTop = 0
        }
    }

    public onActivate() {
        setTimeout(() => {
            window.scroll(0, 0)
        })
        this.preventEmbedCycles()
        this.showServerSetMessages()
    }

    ngAfterViewInit() {
        this.studentGateService.isNavigationStopped.subscribe((value) => {
            if (!this.addOnService.isAddonView) {
                this.isNavigationStopped = value
                this.cdr.detectChanges()
            }
        })
    }

    private preventEmbedCycles() {
        try {
            if (window.top !== window.self && window.top.location.host === window.location.host) {
                /* The current application is embeded in an iframe, aka "double header bug", most likely due to an
                 * old site navigation issue. We want to load the most recent viewed page in the top browser window.
                 * We also, only want to do this the current application is embedded on the same domain.
                 */
                window.top.location.href = window.self.location.href
            }
        } catch (err) {
            console.info("Cannot access window top from iframe. Skipping preventEmbedCycles")
        }
    }

    private showServerSetMessages() {
        this.messageService.getMessages().subscribe((response) => {
            response.results.forEach((item) => {
                this.showMessage(item)
            })
        })
    }

    private showMessage(messageItem) {
        // TODO: This if/else is a minor hack
        // introduced in https://projects.pbs.org/jira/browse/PBSLM-14097
        // The NotificationModalCompoment was desired for the nested collection
        // redirect message, but currently there's no design for all possible
        // types of messages that could be displayed (error/warning/info/success)
        // so we shouldn't replace the old notifications everywhere
        // until we have a design
        if (messageItem.show_in_modal) {
            this.modalService.open(NotificationModalComponent, {
                data: { message: messageItem.message },
            })
        } else {
            const params = { timeOut: 10000, showProgressBar: false }
            let type
            if (messageItem.tags.indexOf("error") !== -1) {
                type = "error"
            } else if (messageItem.tags.indexOf("success") !== -1) {
                type = "success"
            } else if (messageItem.tags.indexOf("warn") !== -1) {
                type = "warn"
            } else {
                type = "info"
            }
            this.notificationService.html(messageItem.message, type, params)
        }
    }

    private configureTooltip() {
        this.tooltipConfig.triggers = "hover"
        this.tooltipConfig.openDelay = 300
        this.tooltipConfig.container = "body"
        this.tooltipConfig.placement = "top"
    }
}
